"use client";

import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { Button } from "../Button";
import { CarouselContext, useCarousel } from "../../hooks/useCarousel";
import { useHasMultipleSlides } from "../../hooks/useHasMultipleSlides";
import { CAROUSEL_STYLES } from "./constants";
import type { ImageProps } from "./types";
import {
  CarouselApi,
  CarouselProps,
  CarouselContentProps,
  CarouselItemProps,
  CarouselPreviousProps,
  CarouselNextProps,
} from "./types";

const DefaultImage: React.FC<ImageProps> = ({
  src,
  alt,
  className,
  fill,
  sizes,
  priority,
  draggable,
}) => (
  <img
    src={typeof src === "string" ? src : src.src}
    alt={alt}
    className={className}
    style={
      fill
        ? {
          position: "absolute",
          height: "100%",
          width: "100%",
          objectFit: "cover",
        }
        : undefined
    }
    sizes={sizes}
    loading={priority ? "eager" : "lazy"}
    draggable={draggable}
  />
);

const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      imageMode,
      images,
      aspectRatio = "aspect-[16/9]",
      imageClassName = "",
      renderTopLeftContent,
      ImageComponent = DefaultImage,
      showArrows = true,
      onClick,
      ...props
    },
    ref
  ) => {
    const preventPropagation = React.useCallback((e: React.SyntheticEvent) => {
      e.stopPropagation();
    }, []);

    const handleContainerClick = React.useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (onClick) {
          e.stopPropagation();
          e.preventDefault();
          onClick(e);
        }
      },
      [onClick]
    );

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn(
            CAROUSEL_STYLES.root,
            imageMode && aspectRatio,
            className
          )}
          role="region"
          aria-roledescription="carousel"
          onClick={imageMode ? handleContainerClick : undefined}
          onMouseDown={imageMode ? preventPropagation : undefined}
          onTouchStart={imageMode ? preventPropagation : undefined}
          {...props}
        >
          {imageMode ? (
            <>
              <div
                onClick={preventPropagation}
                onMouseDown={preventPropagation}
                onTouchStart={preventPropagation}
              >
                <div className="h-full rounded-lg overflow-hidden">
                  <CarouselContent
                    onClick={preventPropagation}
                    onMouseDown={preventPropagation}
                    onTouchStart={preventPropagation}
                  >
                    {images && images.length > 0 ? (
                      images.map((image, index) => (
                        <CarouselItem
                          key={index}
                          className="relative h-full"
                          onClick={preventPropagation}
                          onMouseDown={preventPropagation}
                          onTouchStart={preventPropagation}
                        >
                          <div
                            onClick={preventPropagation}
                            onMouseDown={preventPropagation}
                            onTouchStart={preventPropagation}
                          >
                            <ImageComponent
                              alt={image.alt || `Image ${index + 1}`}
                              src={image.url}
                              fill
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              className={cn("object-cover", imageClassName)}
                              priority={index === 0}
                              draggable={false}
                            />
                          </div>
                        </CarouselItem>
                      ))
                    ) : (
                      <CarouselItem className="h-full">
                        <div className="w-full h-full bg-gray-200" />
                      </CarouselItem>
                    )}
                  </CarouselContent>

                  {showArrows && images && images.length > 1 && (
                    <>
                      <CarouselPrevious
                        onClick={preventPropagation}
                        onMouseDown={preventPropagation}
                        onTouchStart={preventPropagation}
                        className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/80 hover:bg-white/90"
                      />
                      <CarouselNext
                        onClick={preventPropagation}
                        onMouseDown={preventPropagation}
                        onTouchStart={preventPropagation}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/80 hover:bg-white/90"
                      />
                    </>
                  )}
                </div>
              </div>

              {renderTopLeftContent && (
                <div
                  className="absolute left-2 top-2 z-20"
                  onMouseDown={preventPropagation}
                  onTouchStart={preventPropagation}
                >
                  {renderTopLeftContent()}
                </div>
              )}
            </>
          ) : (
            children
          )}
        </div>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ className, viewportClassName, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div
        ref={carouselRef}
        className={cn(CAROUSEL_STYLES.content.container, viewportClassName)}
      >
        <div
          ref={ref}
          className={cn(
            CAROUSEL_STYLES.content.inner,
            orientation === "vertical" && CAROUSEL_STYLES.content.innerVertical,
            className
          )}
          {...props}
        />
      </div>
    );
  }
);
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          CAROUSEL_STYLES.item.base,
          className
        )}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  CarouselPreviousProps
>(({ className, variant = "outline", size = "icon", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev, api } = useCarousel();
  const hasMultipleSlides = useHasMultipleSlides(api);

  if (!hasMultipleSlides || !canScrollPrev) return null;

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        CAROUSEL_STYLES.button.base,
        orientation === "horizontal"
          ? CAROUSEL_STYLES.button.previousHorizontal
          : CAROUSEL_STYLES.button.previousVertical,
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <LuArrowLeft color="#104977" className={CAROUSEL_STYLES.button.icon} />
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNextProps>(
  ({ className, variant = "outline", size = "icon", ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext, api } = useCarousel();
    const hasMultipleSlides = useHasMultipleSlides(api);

    if (!hasMultipleSlides || !canScrollNext) return null;

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          CAROUSEL_STYLES.button.base,
          orientation === "horizontal"
            ? CAROUSEL_STYLES.button.nextHorizontal
            : CAROUSEL_STYLES.button.nextVertical,
          className
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <LuArrowRight color="#104977" className={CAROUSEL_STYLES.button.icon} />
      </Button>
    );
  }
);
CarouselNext.displayName = "CarouselNext";

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
